import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';

import {
  Button,
  Card,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel
} from '@material-ui/core';
import {
  AssignmentReturned as AssignmentReturnedIcon,
  Close as CloseIcon
} from '@material-ui/icons';

import { ApiParams } from '../../../common/api/api';
import CardTitle from '../../../common/card/CardTitle';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import WrappedTableCell from '../../../common/WrappedTableCell';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { ProductBoardColumnEntity } from '../board/ProductBoardColumn';
import UnlinkConfirmDialog from '../dialogs/UnlinkConfirmDialog';
import {
  useFindProductDesignSpecsByDesignOutputId,
  useProductEntityPrefix
} from '../Product.hooks';
import { BaseProductDesignOutputCardProps } from '../Product.interface';
import { getProductEntityName } from '../Product.config';
import DesignOutputAssignDesignSpecDialog from '../dialogs/DesignOutputAssignDesignSpecDialog';

const DesignOutputDesignSpecReferenceCard: React.FC<BaseProductDesignOutputCardProps> =
  (props: BaseProductDesignOutputCardProps) => {
    const {
      productId,
      productDesignOutput,
      productDesignOutputVersion,
      onUpdate,
      onError
    } = props;
    const history = useHistory();
    const [openDialog, setOpenDialog] = useState(false);
    const [itemToUnlink, setItemToUnlink] = useState<number | undefined>();
    const [order, setOrder] = useState<string | undefined>();
    const [orderDirection, setOrderDirection] = useState<
      'ASC' | 'DESC' | undefined
    >();
    const {
      productDesignSpecList,
      isLoadingProductDesignSpecs,
      loadingProductDesignSpecsError,
      findProductDesignSpecs
    } = useFindProductDesignSpecsByDesignOutputId(
      productId,
      productDesignOutput.id
    );

    const { isFieldDisabled } = useTaskEntityFields(productDesignOutput.task);
    const prefix: string = useProductEntityPrefix(
      ProductBoardColumnEntity.DesignSpec
    );

    const handleSort = (column: string) => {
      setOrder(column);
      setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
    };

    const getSortDirection = (column: string): 'asc' | 'desc' => {
      return order === column && orderDirection === 'DESC' ? 'desc' : 'asc';
    };

    const findProductDesignSpecsWithParams = useCallback(() => {
      const params: ApiParams = { getFromRelation: true };
      if (order) params.order = order;
      if (orderDirection) params.direction = orderDirection;
      if (productDesignOutputVersion)
        params.version = productDesignOutputVersion.version;
      findProductDesignSpecs(params);
    }, [
      findProductDesignSpecs,
      order,
      orderDirection,
      productDesignOutputVersion
    ]);

    useEffect(() => {
      findProductDesignSpecsWithParams();
    }, [findProductDesignSpecsWithParams]);

    useEffect(() => {
      if (loadingProductDesignSpecsError) {
        onError?.(loadingProductDesignSpecsError);
      }
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [loadingProductDesignSpecsError]);
    const disabled =
      Boolean(productDesignOutputVersion) ||
      isFieldDisabled('assignDesignSpec');
    return (
      <Card>
        <CardTitle>
          <AssignmentReturnedIcon />
          {getProductEntityName('designSpec')} Reference
        </CardTitle>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell width={24}>
                  <TableSortLabel
                    active={order === 'productDesignSpec.id'}
                    direction={getSortDirection('productDesignSpec.id')}
                    onClick={() => handleSort('productDesignSpec.id')}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'productDesignSpec.title'}
                    direction={getSortDirection('productDesignSpec.title')}
                    onClick={() => handleSort('productDesignSpec.title')}
                  >
                    Title
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={order === 'task.status'}
                    direction={getSortDirection('task.status')}
                    onClick={() => handleSort('task.status')}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoadingProductDesignSpecs && (
                <TableRow>
                  <TableCell colSpan={3} padding='none'>
                    <LinearProgress />
                  </TableCell>
                </TableRow>
              )}
              {productDesignSpecList ? (
                productDesignSpecList.map(designSpec => (
                  <TableRow
                    key={designSpec.id}
                    hover
                    onClick={() => {
                      history.push(
                        `/portal/products/${productId}/design-specs/${designSpec.id}`
                      );
                    }}
                  >
                    <TableCell>{prefix + designSpec.id}</TableCell>
                    <WrappedTableCell>{designSpec.title}</WrappedTableCell>
                    <TableCell>
                      <TaskStatusLabel task={designSpec.task} />
                    </TableCell>
                    <TableCell
                      style={{
                        textAlign: 'center'
                      }}
                    >
                      {!disabled && (
                        <CloseIcon
                          onClick={event => {
                            event.stopPropagation();
                            setItemToUnlink(designSpec.id);
                          }}
                        />
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={3}>
                    No {getProductEntityName('designSpec')}
                  </TableCell>
                </TableRow>
              )}
              <TableRow>
                <TableCell colSpan={2}>
                  <Button
                    variant='outlined'
                    color='primary'
                    disabled={disabled}
                    onClick={() => setOpenDialog(true)}
                  >
                    Assign {getProductEntityName('designSpec')}
                  </Button>
                </TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
        {openDialog && (
          <DesignOutputAssignDesignSpecDialog
            open={openDialog}
            productId={productId}
            designOutputId={productDesignOutput.id}
            selectedItems={productDesignSpecList}
            onError={onError}
            onCancel={() => setOpenDialog(false)}
            onAssigned={() => {
              findProductDesignSpecsWithParams();
              onUpdate?.();
              setOpenDialog(false);
            }}
          />
        )}
        <UnlinkConfirmDialog
          open={!!itemToUnlink}
          productId={productId}
          entityId={productDesignOutput.id}
          from={ProductBoardColumnEntity.DesignOutput}
          to={ProductBoardColumnEntity.DesignSpec}
          unlinkItemId={itemToUnlink}
          selectedItems={productDesignSpecList}
          onError={onError}
          onCancel={() => setItemToUnlink(undefined)}
          onChange={() => {
            setItemToUnlink(undefined);
            findProductDesignSpecsWithParams();
            onUpdate?.();
          }}
        />
      </Card>
    );
  };

export default DesignOutputDesignSpecReferenceCard;
